// TODO [tags]: probably delete this file as we're not soliciting tags AFTER checkout anymore.
//  plus tags are not a requirement any more.

/**
 * @file Require identities with Tags (via matched :albumFolderId).
 */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import get from 'lodash.get';

import { AlbumFolderWithCreatorBrandDetails } from '../../../graphql/queries/album-folder';
import { MeWithIdentitiesWithTags } from '../../../graphql/queries/account';
import {
  isAlbumFolderTagSelectionRequiredForCheckout,
  canSelectActivitities,
} from '../../../util/album-folder';
import Spinner from '../../global/Spinner';
import {
  identitiesFilteredOnAccess,
  sortByIdentityRelationship,
} from '../../../util/identity';
import { getIdentitiesForTagSelection } from '../../../util/purchase-album-folder';
import { AlbumFolderTags } from '../../../graphql/queries/album-folder-tags';
import { resetTagSelection as resetTagSelectionAction } from '../../../actions/identity-tag-selection';

/**
 * Higher order component for the require identities tags functionality.
 *
 * @param {React.Component} WrappedComponent The component to be composed.
 * @returns {React.Component} The composed component.
 */
const WithRequireIdentitiesTags = WrappedComponent =>
  class WithRequireIdentitiesTagsClass extends Component {
    static propTypes = {
      rootAlbumFolder: PropTypes.shape({
        id: PropTypes.string.isRequired,
        album_subtype: PropTypes.string,
      }),
      identities: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          purchased_album_folders: PropTypes.shape({
            records: PropTypes.arrayOf(
              PropTypes.shape({
                id: PropTypes.string,
              }),
            ),
          }),
        }),
      ),
      subscribedAlbumFolderIdentities: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          album_folder: PropTypes.shape({
            id: PropTypes.string,
          }),
          identity: PropTypes.shape({
            id: PropTypes.string,
          }),
        }),
      ),
      identitiesForPurchase: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
        }),
      ),
      loadingIdentities: PropTypes.bool,
      loadingAlbumFolder: PropTypes.bool,
      loadingTags: PropTypes.bool,
      tags: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
        }),
      ),
      resetTagSelection: PropTypes.func,
    };

    componentDidMount() {
      const { resetTagSelection } = this.props;
      resetTagSelection();
    }

    render() {
      const {
        rootAlbumFolder,
        loadingAlbumFolder,
        loadingIdentities,
        loadingTags,
        identities,
        subscribedAlbumFolderIdentities,
        identitiesForPurchase,
        tags,
      } = this.props;

      const loadingClasses = 'justify-content-center align-items-center';

      if (loadingAlbumFolder) {
        return (
          <div className={loadingClasses}>
            <Spinner />
          </div>
        );
      }

      if (!isAlbumFolderTagSelectionRequiredForCheckout(rootAlbumFolder)) {
        return <WrappedComponent {...this.props} />;
      }

      if (loadingIdentities || loadingTags) {
        return (
          <div className={loadingClasses}>
            <Spinner />
          </div>
        );
      }

      const filteredIdentities = getIdentitiesForTagSelection({
        identities,
        subscribedAlbumFolderIdentities,
        albumFolder: rootAlbumFolder,
        identitiesForPurchase,
      });

      // Ensure each of the identities have tags:
      const identityWithoutTags = filteredIdentities.find(
        identity => get(identity, 'album_folder_tags.records', []).length === 0,
      );

      return canSelectActivitities(tags) && identityWithoutTags ? (
        <Redirect
          to={`/purchase/album-folder/${rootAlbumFolder.id}/activities`}
        />
      ) : (
        <WrappedComponent {...this.props} />
      );
    }
  };

/**
 * Higher order container for the require valid album folder functionality.
 *
 * @param {React.Component} WrappedComponent The component to be composed.
 * @returns {Function} The HOC container.
 */
const RequireIdentitiesTags = WrappedComponent => {
  const WithIdentitiesTags = WithRequireIdentitiesTags(WrappedComponent);

  const withAlbumFolderData = graphql(AlbumFolderWithCreatorBrandDetails, {
    options: ({ match }) => ({
      variables: {
        id: match.params.albumFolderId,
      },
    }),
    props: ({ data: { albumFolder, loading } }) => ({
      rootAlbumFolder: get(albumFolder, 'root'),
      loadingAlbumFolder: loading,
    }),
  });

  const withAlbumFolderTags = graphql(AlbumFolderTags, {
    options: ({ rootAlbumFolder }) => ({
      variables: {
        id: rootAlbumFolder.id,
      },
    }),
    props: ({ data: { albumFolder, loading } }) => ({
      loadingTags: loading,
      tags: get(albumFolder, 'tags.records', []),
    }),
    skip: ({ loadingAlbumFolder }) => loadingAlbumFolder,
  });

  const withUserWithIdentitiesTags = graphql(MeWithIdentitiesWithTags, {
    options: ({ match }) => ({
      // Do not use this from cache in case it
      // hasn't been updated
      fetchPolicy: 'network-only',
      variables: {
        albumFolderId: match.params.albumFolderId,
        albumFolderIds: [match.params.albumFolderId],
      },
    }),
    props: ({ data: { me, loading }, ownProps }) => ({
      identities: sortByIdentityRelationship(
        identitiesFilteredOnAccess(
          get(me, 'linked_identities.records', []),
          get(ownProps, 'albumFolder.album_identity_access'),
        ),
      ),
      subscribedAlbumFolderIdentities: get(
        me,
        'subscribed_album_folder_identities.records',
        [],
      ),
      loadingIdentities: loading,
    }),
    skip: ({ rootAlbumFolder }) =>
      !rootAlbumFolder ||
      !isAlbumFolderTagSelectionRequiredForCheckout(rootAlbumFolder),
  });

  const mapStateToProps = ({ discoverAlbumFolder }) => ({
    identitiesForPurchase: discoverAlbumFolder.identities,
  });

  const mapDispatchToProps = {
    resetTagSelection: resetTagSelectionAction,
  };

  return compose(
    connect(mapStateToProps, mapDispatchToProps),
    withAlbumFolderData,
    withAlbumFolderTags,
    withUserWithIdentitiesTags,
  )(WithIdentitiesTags);
};

export { WithRequireIdentitiesTags };
export default RequireIdentitiesTags;
